import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Node, Edge, useNodesState, useEdgesState, addEdge, Connection, MarkerType, NodeChange, applyNodeChanges } from 'reactflow';
import FlowCanvas from './FlowCanvas';
import NodePanel from './NodePanel';
import InteractiveBrowser from './InteractiveBrowser';
import BottomPanel, { OutputLog } from './BottomPanel';
import { API_BASE } from '../config';
import { Action, NodeData, SavedWorkflow, WorkflowVariable } from '../types/workflow';
import { createWorkflow, updateWorkflow } from '../services/workflowService';
import { startInteractiveSession, playActionInSession } from '../services/interactiveService';

const LOOP_CONTAINER_DEFAULT_WIDTH = 440;
const LOOP_CONTAINER_DEFAULT_HEIGHT = 320;

interface FlowDesignerProps {
    workflow?: SavedWorkflow;
    onBack: () => void;
    onSaved: () => void;
}

let nodeId = 0;
const getId = () => `node_${nodeId++}`;

const FlowDesigner: React.FC<FlowDesignerProps> = ({ workflow, onBack, onSaved }) => {
    const [nodes, setNodes, onNodesChange] = useNodesState(workflow?.nodes || []);
    const [edges, setEdges, onEdgesChange] = useEdgesState(workflow?.edges || []);
    const [workflowName, setWorkflowName] = useState(workflow?.name || '');
    const [variables, setVariables] = useState<WorkflowVariable[]>(workflow?.variables || []);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [interactiveSessionId, setInteractiveSessionId] = useState<string | null>(null);
    const [interactiveVncUrl, setInteractiveVncUrl] = useState<string | null>(null);
    const [pickModeRequested, setPickModeRequested] = useState(false);
    const [pendingPickNodeId, setPendingPickNodeId] = useState<string | null>(null);
    const [pendingParentLocator, setPendingParentLocator] = useState<{ method: string; value: string } | null>(null);
    const pendingPickNodeIdRef = useRef<string | null>(null);
    const [activePickFrameIndex, setActivePickFrameIndex] = useState<number>(0);
    const [activePickIsNewWindow, setActivePickIsNewWindow] = useState<boolean>(false);
    const activePickFrameIndexRef = useRef(0);
    const activePickIsNewWindowRef = useRef(false);
    const restoreInteractiveBrowserRef = useRef<(() => void) | null>(null);
    const [nodePlayStatus, setNodePlayStatus] = useState<Record<string, 'idle' | 'playing' | 'success' | 'error'>>({});
    const [nodePlayMessage, setNodePlayMessage] = useState<Record<string, string>>({});
    const handleNodePlayRef = useRef<(nodeId: string) => void>(() => {});
    const edgesRef = useRef(edges);
    const [runtimeVariables, setRuntimeVariables] = useState<Record<string, any>>({});
    const [outputLogs, setOutputLogs] = useState<OutputLog[]>([]);

    useEffect(() => { pendingPickNodeIdRef.current = pendingPickNodeId; }, [pendingPickNodeId]);
    useEffect(() => { activePickFrameIndexRef.current = activePickFrameIndex; }, [activePickFrameIndex]);
    useEffect(() => { activePickIsNewWindowRef.current = activePickIsNewWindow; }, [activePickIsNewWindow]);
    useEffect(() => { edgesRef.current = edges; }, [edges]);

    useEffect(() => {
        if (workflow?.nodes) {
            const maxId = workflow.nodes.reduce((max: number, n: any) => {
                const num = parseInt(n.id.replace('node_', ''));
                return isNaN(num) ? max : Math.max(max, num);
            }, 0);
            nodeId = maxId + 1;
        }
    }, [workflow]);

    useEffect(() => { setHasUnsavedChanges(true); }, [nodes, edges, workflowName]);

    const appendLog = useCallback((level: OutputLog['level'], message: string, nodeLabel?: string) => {
        const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
        setOutputLogs(prev => [...prev, { timestamp, level, message, nodeLabel }]);
    }, []);

    const onConnect = useCallback(
        (params: Connection) => setEdges((eds) => addEdge(params, eds)),
        [setEdges]
    );

    const getActionLabel = (action: Action): string => {
        switch (action.type) {
            case 'open_browser': return action.url ? `Open: ${action.url}` : 'Open Browser';
            case 'close_browser': return 'Close Browser';
            case 'click': return action.locator?.value ? `Click: ${action.locator.value.substring(0, 30)}...` : 'Click';
            case 'hover': return action.locator?.value ? `Hover: ${action.locator.value.substring(0, 30)}...` : 'Hover';
            case 'write': return action.text ? `Write: ${action.text}` : 'Write text';
            case 'sleep': return `Sleep: ${action.duration}s`;
            case 'wait_for_url_change': return `Wait URL Change: ${action.timeout || 30}s`;
            case 'wait_for_element': return `Wait ${action.wait_type === 'disappear' ? 'Disappear' : 'Appear'}: ${action.locator?.value?.substring(0, 25) || 'element'}`;
            case 'get_element':
            case 'get_elements': return action.store_as ? `Store as: ${action.store_as}` : action.type;
            case 'api_call': return action.url ? `${action.method}: ${action.url}` : 'API Call';
            case 'last_file': return action.store_as ? `Last File → ${action.store_as}` : 'Last File';
            case 'rename_file': return action.old_name ? `Rename: ${action.old_name}` : 'Rename File';
            case 'create_file': return action.file_name ? `Create ${(action.file_type || 'pdf').toUpperCase()}: ${action.file_name}` : 'Create File';
            case 'read_file': return action.file_name ? `Read ${(action.file_type || 'pdf').toUpperCase()}: ${action.file_name}` : 'Read File';
            case 'ai_agent': return action.store_as ? `AI Agent → ${action.store_as}` : 'AI Agent';
            case 'loop': return action.iterate_over ? `${action.iterate_over}${action.store_current_as ? ` → ${action.store_current_as}` : ''}` : 'For Each Loop';
            case 'switch_to_new_window': return 'Switch to New Window';
            case 'switch_to_frame': return action.frame_index === -1 ? 'Switch to Main Page' : action.frame_name !== undefined ? `Switch to Frame: ${action.frame_name || '(name)'}` : `Switch to Frame ${action.frame_index ?? 0}`;
            case 'code': return action.code ? `Code: ${action.code.split('\n')[0].substring(0, 30)}...` : 'Code';
            default: return action.type;
        }
    };

    const handleDeleteNode = useCallback((nodeId: string) => {
        setNodes((nds) => {
            // Also delete all children of this node (loop container children)
            const childIds = new Set(nds.filter(n => n.parentId === nodeId).map(n => n.id));
            return nds.filter((node) => node.id !== nodeId && !childIds.has(node.id));
        });
        setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    }, [setNodes, setEdges]);

    const handleAddNode = useCallback((action: Action, position: { x: number; y: number }, connectFromNodeId?: string | null, parentId?: string | null) => {
        const newNodeId = getId();

        if (action.type === 'loop') {
            // Create as a loop container node
            const loopNode: Node<NodeData> = {
                id: newNodeId,
                type: 'loopContainer',
                position,
                style: { width: LOOP_CONTAINER_DEFAULT_WIDTH, height: LOOP_CONTAINER_DEFAULT_HEIGHT },
                data: { label: getActionLabel(action), action, onEdit: () => {}, onDelete: handleDeleteNode },
            };
            setNodes((nds) => nds.concat(loopNode));

            // Auto-connect to previous tail node
            if (connectFromNodeId) {
                setEdges((eds) => [...eds, {
                    id: `e-${connectFromNodeId}-${newNodeId}`,
                    source: connectFromNodeId, target: newNodeId,
                    type: 'smoothstep', animated: true,
                    markerEnd: { type: MarkerType.ArrowClosed },
                } as Edge]);
            }
            return;
        }

        const newNode: Node<NodeData> = {
            id: newNodeId,
            type: 'custom',
            position,
            data: { label: getActionLabel(action), action, onEdit: () => {}, onDelete: handleDeleteNode },
            ...(parentId ? { parentId, extent: 'parent' as const } : {}),
        };
        setNodes((nds) => nds.concat(newNode));

        // Only auto-connect top-level nodes (not loop children)
        if (connectFromNodeId && !parentId) {
            setEdges((eds) => [...eds, {
                id: `e-${connectFromNodeId}-${newNodeId}`,
                source: connectFromNodeId, target: newNodeId,
                type: 'smoothstep', animated: true,
                markerEnd: { type: MarkerType.ArrowClosed },
            } as Edge]);
        }

        // Auto-connect child nodes inside a loop container to the last sibling
        if (parentId) {
            setEdges((currentEdges) => {
                const siblingNodes = nodes.filter(n => n.parentId === parentId);
                if (siblingNodes.length === 0) return currentEdges;

                const siblingIds = new Set(siblingNodes.map(n => n.id));
                const sourcesWithinParent = new Set(
                    currentEdges.filter(e => siblingIds.has(e.source) && siblingIds.has(e.target)).map(e => e.source)
                );
                const tailSiblings = siblingNodes.filter(n => !sourcesWithinParent.has(n.id));
                const tailSibling = tailSiblings.sort((a, b) => b.position.y - a.position.y)[0];

                if (!tailSibling) return currentEdges;
                const edgeId = `e-${tailSibling.id}-${newNodeId}`;
                if (currentEdges.some(e => e.id === edgeId)) return currentEdges;
                return [...currentEdges, {
                    id: edgeId,
                    source: tailSibling.id, target: newNodeId,
                    type: 'smoothstep', animated: true,
                    markerEnd: { type: MarkerType.ArrowClosed },
                } as Edge];
            });
        }

        // Auto-resize the parent loop container so the new child fits
        if (parentId) {
            setNodes((nds) => nds.map((n) => {
                if (n.id !== parentId) return n;
                const PADDING = 60;
                const childW = 200; const childH = 80;
                const minW = Math.max(n.style?.width as number || LOOP_CONTAINER_DEFAULT_WIDTH, position.x + childW + PADDING);
                const minH = Math.max(n.style?.height as number || LOOP_CONTAINER_DEFAULT_HEIGHT, position.y + childH + PADDING);
                return { ...n, style: { ...n.style, width: minW, height: minH } };
            }));
        }
    }, [nodes, setNodes, setEdges, handleDeleteNode]);

    const handleUpdateNode = useCallback((nodeId: string, action: Action) => {
        setNodes((nds) => nds.map((node) =>
            node.id === nodeId ? { ...node, data: { ...node.data, action, label: getActionLabel(action) } } : node
        ));
    }, [setNodes]);

    const handleNodePlay = useCallback(async (nodeId: string) => {
        const node = nodes.find(n => n.id === nodeId);
        if (!node) return;

        // Session-independent actions handled via direct API calls
        const sessionIndependentDirect = ['last_file', 'rename_file', 'create_file', 'read_file', 'ai_agent', 'api_call', 'webhook', 'code'];
        if (sessionIndependentDirect.includes(node.data.action.type)) {
            if (!workflow?.id && ['last_file', 'rename_file', 'create_file', 'read_file', 'webhook'].includes(node.data.action.type)) return;
            setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'playing' }));
            setNodePlayMessage(prev => ({ ...prev, [nodeId]: '' }));
            try {
                const designVars: Record<string, string> = {};
                for (const v of variables) designVars[v.name] = v.value.replace(/^["']|["']$/g, '');
                const allVars = { ...designVars, ...runtimeVariables };
                const resolveVars = (s: string) => s.replace(/\{\{(\w+)\}\}/g, (_, name: string) => allVars[name] !== undefined ? String(allVars[name]) : `{{${name}}}`);
                const action = node.data.action;

                if (action.type === 'last_file') {
                    const res = await fetch(`${API_BASE}/workflows/${workflow!.id}/storage/last-file`, { method: 'POST' });
                    const result = await res.json();
                    if (result.success && action.store_as) {
                        setRuntimeVariables(prev => ({ ...prev, [action.store_as!]: result.filename }));
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'success' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: `Last file: ${result.filename}` }));
                    } else {
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'error' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message || 'No files found' }));
                    }
                } else if (action.type === 'rename_file') {
                    const res = await fetch(`${API_BASE}/workflows/${workflow!.id}/storage/rename`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ oldName: resolveVars(action.old_name || ''), newName: resolveVars(action.new_name || '') }),
                    });
                    const result = await res.json();
                    if (result.success) {
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'success' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message }));
                    } else {
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'error' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message }));
                    }
                } else if (action.type === 'create_file') {
                    const dataVar = action.data || '';
                    const dataValue = allVars[dataVar];
                    if (dataValue === undefined) throw new Error(`Variable "${dataVar}" is not set`);
                    const res = await fetch(`${API_BASE}/workflows/${workflow!.id}/storage/create-file`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ file_type: action.file_type || 'pdf', data: dataValue, file_name: resolveVars(action.file_name || '') }),
                    });
                    const result = await res.json();
                    if (result.success) {
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'success' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message }));
                    } else {
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'error' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message }));
                    }
                } else if (action.type === 'read_file') {
                    const res = await fetch(`${API_BASE}/workflows/${workflow!.id}/storage/read-file`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ file_type: action.file_type || 'pdf', file_name: resolveVars(action.file_name || '') }),
                    });
                    const result = await res.json();
                    if (result.success) {
                        if (action.store_as) {
                            setRuntimeVariables(prev => ({
                                ...prev,
                                [action.store_as!]: typeof result.data === 'string' ? result.data : JSON.stringify(result.data),
                            }));
                        }
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'success' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message }));
                    } else {
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'error' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message }));
                    }
                } else if (action.type === 'ai_agent') {
                    const res = await fetch(`${API_BASE}/interactive/ai-agent`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            system_prompt: action.system_prompt || '',
                            user_prompt: action.user_prompt || '',
                            variables: allVars,
                            output_type: action.output_type || 'string',
                        }),
                    });
                    const result = await res.json();
                    if (result.success) {
                        if (action.store_as) {
                            setRuntimeVariables(prev => ({ ...prev, [action.store_as!]: result.response }));
                        }
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'success' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message }));
                    } else {
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'error' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message }));
                    }
                } else if (action.type === 'api_call') {
                    const res = await fetch(`${API_BASE}/interactive/api-call`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            method: action.method || 'GET',
                            url: action.url || '',
                            headers: action.headers || {},
                            requestBody: action.body || {},
                            variables: allVars,
                        }),
                    });
                    const result = await res.json();
                    if (result.success) {
                        if (action.store_as) {
                            setRuntimeVariables(prev => ({
                                ...prev,
                                [action.store_as!]: typeof result.responseBody === 'object' ? JSON.stringify(result.responseBody) : String(result.responseBody),
                            }));
                        }
                        if (action.store_status_as) {
                            setRuntimeVariables(prev => ({ ...prev, [action.store_status_as!]: String(result.status) }));
                        }
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'success' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: `${result.status} — ${String(result.responseBody).substring(0, 100)}` }));
                    } else {
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'error' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message }));
                    }
                } else if (action.type === 'webhook') {
                    await fetch(`${API_BASE}/webhooks/${workflow!.id}/test`, { method: 'POST' });
                    const resultRes = await fetch(`${API_BASE}/webhooks/${workflow!.id}/test/wait`);
                    const result = await resultRes.json();
                    if (result.body) {
                        if (action.store_as) {
                            setRuntimeVariables(prev => ({ ...prev, [action.store_as!]: result.body }));
                        }
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'success' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: `Received! ${JSON.stringify(result.body).substring(0, 100)}` }));
                    } else {
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'error' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.error || 'No request received' }));
                    }
                } else if (action.type === 'code') {
                    const res = await fetch(`${API_BASE}/interactive/run-code`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ code: action.code || '', variables: allVars }),
                    });
                    const result = await res.json();
                    if (result.success) {
                        if (result.message && result.message !== 'Code executed successfully (no output)') {
                            const label = node.data.label || 'Code';
                            result.message.split('\n').forEach((line: string) => {
                                if (line.trim()) {
                                    const level = line.startsWith('[error]') ? 'error' : line.startsWith('[warn]') ? 'warn' : 'log';
                                    appendLog(level as OutputLog['level'], line.replace(/^\[(error|warn)\]\s*/, ''), label);
                                }
                            });
                        }
                        // Re-run code locally for variable side-effects
                        try {
                            const updatedVars: Record<string, any> = {};
                            for (const [k, v] of Object.entries(allVars)) {
                                if (typeof v === 'string') {
                                    const t = v.trim();
                                    if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('{') && t.endsWith('}'))) {
                                        try { updatedVars[k] = JSON.parse(t); continue; } catch {}
                                    }
                                    if (t === 'true') { updatedVars[k] = true; continue; }
                                    if (t === 'false') { updatedVars[k] = false; continue; }
                                    if (t !== '' && !isNaN(Number(t))) { updatedVars[k] = Number(t); continue; }
                                }
                                updatedVars[k] = v;
                            }
                            const fn = new Function('variables', 'console', `with(variables) { ${action.code || ''} }`);
                            fn(updatedVars, { log: () => {}, error: () => {}, warn: () => {} });
                            const newRuntime: Record<string, any> = { ...runtimeVariables };
                            for (const [k, v] of Object.entries(updatedVars)) {
                                if (k.startsWith('__')) continue;
                                if (typeof v === 'object') newRuntime[k] = JSON.stringify(v);
                                else if (v !== undefined) newRuntime[k] = String(v);
                            }
                            setRuntimeVariables(newRuntime);
                        } catch (e) {}
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'success' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message || 'Code executed successfully' }));
                    } else {
                        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'error' }));
                        setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message || 'Code failed' }));
                    }
                }
            } catch (err: any) {
                setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'error' }));
                setNodePlayMessage(prev => ({ ...prev, [nodeId]: err.message || 'Unknown error' }));
            }
            return;
        }

        if (!interactiveSessionId) return;
        restoreInteractiveBrowserRef.current?.();
        setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'playing' }));
        setNodePlayMessage(prev => ({ ...prev, [nodeId]: '' }));
        try {
            const designVars: Record<string, string> = {};
            for (const v of variables) designVars[v.name] = v.value.replace(/^["']|["']$/g, '');
            const mergedVars = { ...designVars, ...runtimeVariables };
            const actionWithVars = { ...node.data.action, variables: mergedVars };
            const result = await playActionInSession(interactiveSessionId, actionWithVars);

            if (result.success && (result as any).store_as && (result as any).stored_value !== undefined) {
                setRuntimeVariables(prev => ({
                    ...prev,
                    [(result as any).store_as]: (result as any).stored_value,
                    ...(result as any).stored_locator
                        ? { [`__locator_${(result as any).store_as}`]: JSON.stringify((result as any).stored_locator) }
                        : {},
                }));
            }

            if (node.data.action.type === 'code' && result.message) {
                const label = node.data.label || 'Code';
                result.message.split('\n').forEach((line: string) => {
                    if (line.trim()) {
                        const level = line.startsWith('[error]') ? 'error' : line.startsWith('[warn]') ? 'warn' : 'log';
                        appendLog(level as OutputLog['level'], line.replace(/^\[(error|warn)\]\s*/, ''), label);
                    }
                });
            }

            if (node.data.action.type === 'code' && result.success) {
                try {
                    const updatedVars: Record<string, any> = {};
                    for (const [k, v] of Object.entries(mergedVars)) {
                        if (typeof v === 'string') {
                            const t = v.trim();
                            if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('{') && t.endsWith('}'))) {
                                try { updatedVars[k] = JSON.parse(t); continue; } catch {}
                            }
                            if (t === 'true') { updatedVars[k] = true; continue; }
                            if (t === 'false') { updatedVars[k] = false; continue; }
                            if (t !== '' && !isNaN(Number(t))) { updatedVars[k] = Number(t); continue; }
                        }
                        updatedVars[k] = v;
                    }
                    const fn = new Function('variables', 'console', `with(variables) { ${node.data.action.code || ''} }`);
                    fn(updatedVars, { log: () => {}, error: () => {}, warn: () => {} });
                    const newRuntime: Record<string, any> = { ...runtimeVariables };
                    for (const [k, v] of Object.entries(updatedVars)) {
                        if (k.startsWith('__')) continue;
                        if (typeof v === 'object') newRuntime[k] = JSON.stringify(v);
                        else if (v !== undefined) newRuntime[k] = String(v);
                    }
                    setRuntimeVariables(newRuntime);
                } catch (e) {}
            }

            if (result.success) {
                setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'success' }));
                setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message || 'Action executed successfully' }));
            } else {
                setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'error' }));
                setNodePlayMessage(prev => ({ ...prev, [nodeId]: result.message || 'Action failed' }));
            }
        } catch (err: any) {
            setNodePlayStatus(prev => ({ ...prev, [nodeId]: 'error' }));
            setNodePlayMessage(prev => ({ ...prev, [nodeId]: err.message || 'Unknown error' }));
        }
    }, [nodes, interactiveSessionId, variables, runtimeVariables, appendLog, workflow]);

    useEffect(() => { handleNodePlayRef.current = handleNodePlay; }, [handleNodePlay]);

    useEffect(() => {
        setNodes((nds) => nds.map((node) => ({
            ...node,
            data: {
                ...node.data,
                onEdit: () => {},
                onDelete: handleDeleteNode,
                onPlay: (nodeId: string) => handleNodePlayRef.current(nodeId),
                interactiveSessionId,
                playStatus: nodePlayStatus[node.id] || 'idle',
                playMessage: nodePlayMessage[node.id] || '',
            },
        })));
    }, [handleDeleteNode, setNodes, interactiveSessionId, nodePlayStatus, nodePlayMessage]);

    const cleanAction = (action: Action): Record<string, any> => {
        const clean: Record<string, any> = { type: action.type };
        if (action.locator?.value) clean.locator = action.locator;
        if (action.text) clean.text = action.text;
        if (action.duration !== undefined) clean.duration = action.duration;
        if (action.timeout !== undefined) clean.timeout = action.timeout;
        if (action.frame_index !== undefined) clean.frame_index = action.frame_index;
        if (action.frame_name !== undefined) clean.frame_name = action.frame_name;
        if (action.store_as) clean.store_as = action.store_as;
        if (action.parent) clean.parent = action.parent;
        if (action.regex) clean.regex = action.regex;
        if (action.condition) clean.condition = action.condition;
        if (action.iterate_over) clean.iterate_over = action.iterate_over;
        if (action.store_current_as) clean.store_current_as = action.store_current_as;
        if (action.method) clean.method = action.method;
        if (action.url) clean.url = action.url;
        if (action.headers && Object.keys(action.headers).length > 0) clean.headers = action.headers;
        if (action.body && Object.keys(action.body).length > 0) clean.body = action.body;
        if (action.headers_raw) clean.headers_raw = action.headers_raw;
        if (action.body_raw) clean.body_raw = action.body_raw;
        if (action.store_status_as) clean.store_status_as = action.store_status_as;
        if (action.actions && action.actions.length > 0) clean.actions = action.actions.map(cleanAction);
        if (action.code) clean.code = action.code;
        if (action.old_name) clean.old_name = action.old_name;
        if (action.new_name) clean.new_name = action.new_name;
        if (action.wait_type) clean.wait_type = action.wait_type;
        if (action.file_type) clean.file_type = action.file_type;
        if (action.data) clean.data = action.data;
        if (action.file_name) clean.file_name = action.file_name;
        if (action.system_prompt) clean.system_prompt = action.system_prompt;
        if (action.user_prompt) clean.user_prompt = action.user_prompt;
        if (action.output_type) clean.output_type = action.output_type;
        if (action.use_proxy) {
            clean.use_proxy = true;
            if (action.proxy_host) clean.proxy_host = action.proxy_host;
            if (action.proxy_port) clean.proxy_port = action.proxy_port;
            if (action.proxy_user) clean.proxy_user = action.proxy_user;
            if (action.proxy_password) clean.proxy_password = action.proxy_password;
        }
        return clean;
    };

    const buildActions = (): Action[] => {
        // Top-level flow (nodes with no parentId)
        const topNodes = nodes.filter(n => !n.parentId);
        const topEdges = edges.filter(e => {
            const src = nodes.find(n => n.id === e.source);
            const tgt = nodes.find(n => n.id === e.target);
            return !src?.parentId && !tgt?.parentId;
        });

        const childrenMap = new Map<string, string[]>();
        topEdges.forEach(edge => {
            if (!childrenMap.has(edge.source)) childrenMap.set(edge.source, []);
            childrenMap.get(edge.source)!.push(edge.target);
        });

        const nodeIds = new Set(topNodes.map(n => n.id));
        const targetIds = new Set(topEdges.map(e => e.target));
        const rootIds = Array.from(nodeIds).filter(id => !targetIds.has(id));

        // Get children of a loop container, ordered by their Y position
        const getLoopChildren = (loopNodeId: string): Action[] => {
            const children = nodes
                .filter(n => n.parentId === loopNodeId)
                .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);

            // Build sequence from children using their edges
            const childEdges = edges.filter(e => {
                const src = nodes.find(n => n.id === e.source);
                const tgt = nodes.find(n => n.id === e.target);
                return src?.parentId === loopNodeId && tgt?.parentId === loopNodeId;
            });
            const childChildrenMap = new Map<string, string[]>();
            childEdges.forEach(e => {
                if (!childChildrenMap.has(e.source)) childChildrenMap.set(e.source, []);
                childChildrenMap.get(e.source)!.push(e.target);
            });
            const childTargetIds = new Set(childEdges.map(e => e.target));
            const childRoots = children.filter(n => !childTargetIds.has(n.id));

            const buildChildSeq = (nodeId: string, visited = new Set<string>()): Action[] => {
                if (visited.has(nodeId)) return [];
                visited.add(nodeId);
                const node = nodes.find(n => n.id === nodeId);
                if (!node) return [];
                const results: Action[] = [{ ...node.data.action }];
                for (const childId of childChildrenMap.get(nodeId) || []) results.push(...buildChildSeq(childId, new Set(visited)));
                return results;
            };

            const actions: Action[] = [];
            const visited = new Set<string>();
            const sortedRoots = childRoots.sort((a, b) => a.position.y - b.position.y);
            for (const root of sortedRoots) {
                if (!visited.has(root.id)) actions.push(...buildChildSeq(root.id, visited));
            }
            return actions;
        };

        const buildSequence = (nodeId: string, visited = new Set<string>()): Action[] => {
            if (visited.has(nodeId)) return [];
            visited.add(nodeId);
            const node = topNodes.find(n => n.id === nodeId);
            if (!node) return [];

            let action = { ...node.data.action };

            // For loop container nodes, collect children as action.actions
            if (node.type === 'loopContainer') {
                action = { ...action, actions: getLoopChildren(nodeId) };
            }

            const results: Action[] = [action];
            for (const childId of childrenMap.get(nodeId) || []) results.push(...buildSequence(childId, new Set(visited)));
            return results;
        };

        const actions: Action[] = [];
        const globalVisited = new Set<string>();
        const sortedRoots = rootIds.map(id => topNodes.find(n => n.id === id)!).filter(Boolean).sort((a, b) => a.position.y - b.position.y);
        for (const rootNode of sortedRoots) {
            if (!globalVisited.has(rootNode.id)) actions.push(...buildSequence(rootNode.id, globalVisited));
        }
        return actions;
    };

    const handleSave = async () => {
        if (!workflowName.trim()) { alert('Please enter a workflow name.'); return; }
        const actions = buildActions().map(cleanAction) as Action[];
        try {
            if (workflow) {
                await updateWorkflow(workflow.id, { name: workflowName, url: '', actions, nodes, edges, variables });
            } else {
                await createWorkflow({ name: workflowName, url: '', actions, nodes, edges, variables });
            }
            setHasUnsavedChanges(false);
            onSaved();
        } catch (err) {
            console.error('Failed to save workflow:', err);
            alert('Failed to save workflow. Please try again.');
        }
    };

    const handleExport = () => {
        const actions = buildActions().map(cleanAction);
        const blob = new Blob([JSON.stringify({ actions }, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${(workflowName || 'workflow').replace(/\s+/g, '_').toLowerCase()}.json`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleBack = () => {
        if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Are you sure you want to go back?')) return;
        onBack();
    };

    const handleUndo = () => { if (nodes.length > 0) setNodes((nds) => nds.slice(0, -1)); };

    const handleStartInteractive = async (url: string, proxy?: { host: string; port: string; user: string; password: string }): Promise<void> => {
        try {
            const result = await startInteractiveSession(url, proxy, workflow?.id);
            setInteractiveSessionId(result.sessionId);
            setInteractiveVncUrl(result.vncUrl);
        } catch (err) {
            console.error('Failed to start interactive session:', err);
            alert('Failed to start interactive browser session.');
            throw err;
        }
    };

    const handleCloseInteractive = () => {
        setInteractiveSessionId(null);
        setInteractiveVncUrl(null);
        setPickModeRequested(false);
        setPendingPickNodeId(null);
        setPendingParentLocator(null);
        setActivePickFrameIndex(0);
        activePickFrameIndexRef.current = 0;
        setActivePickIsNewWindow(false);
        activePickIsNewWindowRef.current = false;
        setNodePlayStatus({});
        setNodePlayMessage({});
        setRuntimeVariables({});
        setOutputLogs([]);
    };

    const handlePickElement = (nodeId: string, parentLocator?: { method: string; value: string } | null) => {
        if (!interactiveSessionId) {
            alert('Please open an interactive browser session first.');
            return;
        }
        setPendingPickNodeId(nodeId);
        setPickModeRequested(true);
        setPendingParentLocator(parentLocator || null);
    };

    const handleElementPicked = (locator: { method: string; value: string }, frameIndex?: number, isNewWindow?: boolean, frameName?: string) => {
        const targetNodeId = pendingPickNodeIdRef.current;
        if (!targetNodeId) { setPickModeRequested(false); return; }
        const pickedFrameIndex = frameIndex ?? 0;
        const pickedIsNewWindow = isNewWindow ?? false;
        const prevFrameIndex = activePickFrameIndexRef.current;
        activePickIsNewWindowRef.current = pickedIsNewWindow;
        activePickFrameIndexRef.current = pickedFrameIndex;
        setActivePickIsNewWindow(pickedIsNewWindow);
        setActivePickFrameIndex(pickedFrameIndex);

        setNodes((nds) => {
            const updatedNodes = nds.map((node) => {
                if (node.id === targetNodeId) {
                    const updatedAction = { ...node.data.action, locator: { method: locator.method as any, value: locator.value } };
                    return { ...node, data: { ...node.data, action: updatedAction, label: getActionLabel(updatedAction) } };
                }
                return node;
            });
            const insertSwitchBefore = (switchAction: Action, yOffset = 120) => {
                const targetNode = updatedNodes.find(n => n.id === targetNodeId);
                if (!targetNode) return updatedNodes;
                const switchNodeId = getId();
                const switchNode: Node<NodeData> = {
                    id: switchNodeId, type: 'custom',
                    position: { x: targetNode.position.x, y: targetNode.position.y - yOffset },
                    data: { label: getActionLabel(switchAction), action: switchAction, onEdit: () => {}, onDelete: handleDeleteNode },
                };
                const currentEdges = edgesRef.current;
                const existingEdgeToTarget = currentEdges.find(e => e.target === targetNodeId);
                setEdges((eds) => {
                    const newEdges = eds.filter(e => e.target !== targetNodeId);
                    if (existingEdgeToTarget) newEdges.push({ ...existingEdgeToTarget, id: `e-${existingEdgeToTarget.source}-${switchNodeId}`, target: switchNodeId, markerEnd: { type: MarkerType.ArrowClosed } });
                    newEdges.push({ id: `e-${switchNodeId}-${targetNodeId}`, source: switchNodeId, target: targetNodeId, type: 'smoothstep', animated: true, markerEnd: { type: MarkerType.ArrowClosed } });
                    return newEdges;
                });
                return [...updatedNodes, switchNode];
            };
            if (pickedIsNewWindow && !nds.some(n => n.data?.action?.type === 'switch_to_new_window')) {
                return insertSwitchBefore({ type: 'switch_to_new_window' });
            }
            if (pickedFrameIndex > 0 && pickedFrameIndex !== prevFrameIndex && !nds.some(n => n.data?.action?.type === 'switch_to_frame' && n.data?.action?.frame_index === pickedFrameIndex - 1)) {
                const switchAction: Action = frameName
                    ? { type: 'switch_to_frame', frame_name: frameName }
                    : { type: 'switch_to_frame', frame_index: pickedFrameIndex - 1 };
                return insertSwitchBefore(switchAction);
            }
            if (prevFrameIndex > 0 && pickedFrameIndex === 0) {
                return insertSwitchBefore({ type: 'switch_to_frame', frame_index: -1 });
            }
            return updatedNodes;
        });
        setPendingPickNodeId(null);
        pendingPickNodeIdRef.current = null;
        setPickModeRequested(false);
        setPendingParentLocator(null);
    };

    const handlePlayAction = async (action: Action): Promise<void> => {
        const sessionIndependent = ['api_call', 'webhook', 'code', 'ai_agent'];
        if (!interactiveSessionId && !sessionIndependent.includes(action.type)) throw new Error('No interactive session active');
        restoreInteractiveBrowserRef.current?.();
        const designVars: Record<string, string> = {};
        for (const v of variables) designVars[v.name] = v.value.replace(/^["']|["']$/g, '');
        const mergedVars = { ...designVars, ...runtimeVariables };
        const actionWithVars = { ...action, variables: mergedVars };

        // API Call: handle via backend endpoint (no browser needed)
        if (action.type === 'api_call') {
            const res = await fetch(`${API_BASE}/interactive/api-call`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    method: action.method || 'GET',
                    url: action.url || '',
                    headers: action.headers || {},
                    requestBody: action.body || {},
                    variables: mergedVars,
                }),
            });
            const result = await res.json();
            if (result.success) {
                const newRuntime: Record<string, any> = { ...runtimeVariables };
                if (action.store_as) {
                    newRuntime[action.store_as] = typeof result.responseBody === 'object' ? JSON.stringify(result.responseBody) : String(result.responseBody);
                }
                if (action.store_status_as) {
                    newRuntime[action.store_status_as] = String(result.status);
                }
                setRuntimeVariables(newRuntime);
            } else {
                throw new Error(result.message);
            }
            return;
        }

        if (action.type === 'code') {
            try {
                const updatedVars: Record<string, any> = {};
                for (const [k, v] of Object.entries(mergedVars)) {
                    if (typeof v === 'string') {
                        const t = v.trim();
                        if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('{') && t.endsWith('}'))) {
                            try { updatedVars[k] = JSON.parse(t); continue; } catch {}
                        }
                        if (t === 'true') { updatedVars[k] = true; continue; }
                        if (t === 'false') { updatedVars[k] = false; continue; }
                        if (t !== '' && !isNaN(Number(t))) { updatedVars[k] = Number(t); continue; }
                    }
                    updatedVars[k] = v;
                }
                const fn = new Function('variables', 'console', `with(variables) { ${action.code || ''} }`);
                fn(updatedVars, { log: () => {}, error: () => {}, warn: () => {} });
                const newRuntime: Record<string, string> = { ...runtimeVariables };
                for (const [k, v] of Object.entries(updatedVars)) {
                    if (k.startsWith('__')) continue;
                    if (typeof v === 'object') newRuntime[k] = JSON.stringify(v);
                    else if (v !== undefined) newRuntime[k] = String(v);
                }
                setRuntimeVariables(newRuntime);
            } catch (e) {}
        }

        const result = await playActionInSession(interactiveSessionId!, actionWithVars);

        if (result.success && (result as any).store_as && (result as any).stored_value !== undefined) {
            setRuntimeVariables(prev => ({
                ...prev,
                [(result as any).store_as]: (result as any).stored_value,
                ...(result as any).stored_locator
                    ? { [`__locator_${(result as any).store_as}`]: JSON.stringify((result as any).stored_locator) }
                    : {},
            }));
        }

        if (action.type === 'code' && result.message) {
            result.message.split('\n').forEach((line: string) => {
                if (line.trim()) {
                    const level = line.startsWith('[error]') ? 'error' : line.startsWith('[warn]') ? 'warn' : 'log';
                    appendLog(level as OutputLog['level'], line.replace(/^\[(error|warn)\]\s*/, ''), 'Code (settings)');
                }
            });
        }

        if (!result.success) throw new Error(result.message);
    };

    // Derive pickMulti from the pending node's action type
    const pendingNode = pendingPickNodeId ? nodes.find(n => n.id === pendingPickNodeId) : null;
    const pickMulti = pendingNode?.data?.action?.type === 'get_elements';

    return (
        <div className="app-container">
            <div className="designer-header">
                <button className="btn-back" onClick={handleBack}>← Back</button>
                <input
                    type="text"
                    className="workflow-name-input"
                    value={workflowName}
                    onChange={(e) => setWorkflowName(e.target.value)}
                    placeholder="Enter workflow name..."
                />
                <div className="designer-header-actions">
                    <button className="btn-undo" onClick={handleUndo}>↩ Undo</button>
                    <button className="btn-export-header" onClick={handleExport}>📥 Export JSON</button>
                    <button className="btn-save-header" onClick={handleSave}>💾 Save</button>
                </div>
            </div>
            <div className="workflow-designer" style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
                <NodePanel />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        <FlowCanvas
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            onAddNode={handleAddNode}
                            onUpdateNode={handleUpdateNode}
                            onDeleteNode={handleDeleteNode}
                            onStartInteractive={handleStartInteractive}
                            onPickElement={handlePickElement}
                            onPlayAction={handlePlayAction}
                            interactiveSessionId={interactiveSessionId}
                            variables={variables}
                            runtimeVariables={runtimeVariables}
                            onRuntimeVariablesUpdate={setRuntimeVariables}
                            onAppendLog={appendLog}
                            workflowId={workflow?.id}
                        />
                    </div>
                    <BottomPanel
                        variables={variables}
                        onChange={setVariables}
                        runtimeVariables={runtimeVariables}
                        onRuntimeVariablesUpdate={setRuntimeVariables}
                        outputLogs={outputLogs}
                        onClearLogs={() => setOutputLogs([])}
                        workflowId={workflow?.id}
                    />
                </div>
            </div>
            {interactiveSessionId && interactiveVncUrl && (
                <InteractiveBrowser
                    sessionId={interactiveSessionId}
                    vncUrl={interactiveVncUrl}
                    onClose={handleCloseInteractive}
                    onElementPicked={handleElementPicked}
                    pickModeRequested={pickModeRequested}
                    pickMulti={pickMulti}
                    parentLocator={pendingParentLocator}
                    onRestoreRef={(fn) => { restoreInteractiveBrowserRef.current = fn; }}
                    onPickModeChange={(active) => {
                        if (!active) { setPickModeRequested(false); setPendingPickNodeId(null); setPendingParentLocator(null); }
                    }}
                />
            )}
        </div>
    );
};

export default FlowDesigner;